<template>
  <div class="flex w-full h-full py-8">
    <div class="items-center justify-center w-full h-full flex-wrap py-8">
      <div class="w-full items-center justify-center py-8">
        <img src="@/assets/images/hireful/newlogo.svg" loading="lazy" alt="hireful logo" class="hireful-logo-container" />
      </div>
      <div class="flex w-full items-center justify-center text-hireful hireful-font-semibold hireful-title py-4" v-if="pageTitle">
        <div class="flex w-1/2 items-center justify-start">
          {{pageTitle}}
        </div>
      </div>
      <div class="flex flex-wrap w-full items-center justify-center" v-if="!isNewUser && !setupMFA && !isMfaRequired">
        <div class="flex flex-wrap w-1/2 items-center justify-center" v-if="!multiAccountLogin && !microsoftLoading && !googleLoading">
          <div class="flex flex-wrap w-full items-center justify-center py-4">
            <div class="flex w-full text-gray-400 text-sm py-2 hireful-font-domus">
              Email address
            </div>
            <div class="flex w-full">
              <text-field v-model="user.email" @enter="loginUser()" />
            </div>
          </div>
          <div class="flex flex-wrap w-full items-center justify-center">
            <div class="flex w-full text-gray-400 text-sm py-2 hireful-font-domus">
              Password
            </div>
            <div class="flex w-full relative">
              <text-field :type="passwordFieldType" v-model="user.password" @enter="loginUser()" />
              <div class="flex items-end justify-end cursor-pointer" @click="switchVisibility()" >
                <img src="@/assets/icons/eye.svg"
                class="absolute cursor-pointer h-6 w-6 right-4 top-2 fill-current" v-if="passwordFieldType === 'password'">
                <img src="@/assets/icons/hide-eye.svg"
              class="absolute cursor-pointer h-6 w-6 right-4 top-2" v-else>
              </div>
            </div>
          </div>
          <div class="flex flex-wrap w-full items-center justify-start mt-5">
            <div class="flex w-1/2 items-center justify-start text-base hireful-font-domus">
              <router-link to="forgotPassword">Forgot your password?</router-link>
            </div>
          </div>
          <div class="flex flex-wrap w-full item-center justify-center mt-5" v-if="showNotification">
              <hireful-notification :msg="notificationMessage"></hireful-notification>
          </div>
          <div class="flex w-full items-center justify-center pt-8">
            <loader v-if="loadingLogin" />
            <button v-else class="bg-hireful text-white w-full hireful-font-semibold h-12 rounded-lg"
            @click="loginUser()"
            >Log in</button>
          </div>

          <div class="flex flex-wrap w-full items-center justify-center py-8">
            <div class="relative w-full flex items-center">
              <div class="flex-grow border-t border-gray-400"></div>
              <span class="flex-shrink mx-4 text-gray-400">
                Or continue with
              </span>
              <div class="flex-grow border-t border-gray-400"></div>
            </div>
          </div>

          <div class="flex flex-wrap w-full items-center justify-center">
            <div class="flex flex-wrap w-1/2 px-2 items-center justify-center">
              <button class="flex w-full items-center justify-center p-2 font-bold rounded-lg text-sm border" @click="loginGoogleUser()">
                <loader v-if="googleLoading" />
                <img v-else src="@/assets/icons/googleIcon.svg" class="h-6 w-6">
              </button>
            </div>
            <div class="flex flex-wrap w-1/2 px-2 items-center justify-center">
              <button class="flex w-full items-center justify-center p-2 font-bold rounded-lg text-sm border" @click="loginMicrosoftUser()">
                <loader v-if="microsoftLoading" />
                <img v-else src="@/assets/icons/microsoft-new.png" class="h-6 w-6">
              </button>
            </div>
          </div>

          <div class="flex flex-wrap w-full items-center justify-center py-4">
            <span class="hireful-font-domus text-gray-400">
              Haven't got an account?
            </span>
            <span class="hireful-font-domus text-hireful px-2">
              <a href="https://www.hireful.com/book-a-demo" target="_blank">
                Request a demo
              </a>
            </span>
          </div>
        </div>
        <div v-else-if="microsoftLoading || googleLoading" class="flex w-full items-center justify-center flex-wrap">
            <div class="flex flex-wrap w-full items-center justify-center my-8">
              <loader class="mt-8 w-full" />
            </div>
          </div>
        <div class="flex flex-wrap w-full items-center justify-center" v-else>
          <select-account :accounts="accountsResponse" />
        </div>
      </div>
      <div class="flex flex-wrap w-full items-center justify-center" v-else-if="setupMFA">
        <div class="flex flex-wrap w-1/2 items-center justify-center">
          <enable-mfa @setPageTitle="setPageTitle" @setupMfa="setupMfa" @skipMfa="skipMfa" v-if="!configureMfa && !isMfaEnforced" />
          <setup-mfa :enforced="isMfaEnforced" @setPageTitle="setPageTitle" @back="resetMfa" @continue="continueLoginAfterMfaSetup" v-else />
        </div>
      </div>
      <div class="flex flex-wrap w-full items-center justify-center" v-else-if="isMfaRequired">
        <div class="flex flex-wrap w-1/2 items-center justify-center" v-if="!isMfaCompleted">
          <login-mfa-step :type="mfaType" :details="mfaDetails"
          :showNotification="showNotification"
          :message="notificationMessage"
          :resending="resendingMfa"
          :checkingCode="loadingMfaCheck"
          @rememberDevice="rememberDevice"
          @setPageTitle="setPageTitle"
          @code="submitMfaCode"
          @resend="resendMfaCode" />
        </div>
        <div class="flex flex-wrap w-full items-center justify-center" v-else>

          <div v-if="activeClientList && activeClientList.length === 1" class="flex w-full items-center justify-center flex-wrap">
            <div class="flex flex-wrap w-full items-center justify-center my-8">
              <loader class="mt-8 w-full" />
              <div class="font-bold w-full my-4">
                Logging in
              </div>
            </div>
          </div>
          <select-account v-else :accounts="accountsResponse" />
        </div>
      </div>
      <div row wrap class="flex flex-wrap w-full items-center justify-center" v-else-if="isNewUser">
        <div class="flex flex-wrap w-1/2 items-center justify-center" v-if="!multiAccountLogin">
          <div class="flex w-full items-center justify-center text-left hireful-font-domus text-gray-400 py-4">
            Please provide a password that contains at least 8 or more characters and contain a mixture of uppercase, lowercase, symbol and number characters.
          </div>
          <div class="flex flex-wrap w-full items-center justify-center">
              <div class="flex w-full text-gray-400 text-sm py-2 hireful-font-domus">
                New password
                <span class="text-hireful-yellow"> *</span>
              </div>
              <div class="flex w-full relative">
                <text-field :type="passwordFieldType" v-model="newPassword" autocomplete="new-password" @enter="updatePassword()" />
              </div>
            </div>
            <div class="flex flex-wrap w-full items-center justify-center py-4">
              <div class="flex w-full text-gray-400 text-sm py-2 hireful-font-domus">
                Confirm new password
                <span class="text-hireful-yellow"> *</span>
              </div>
              <div class="flex w-full relative">
                <text-field :type="passwordFieldType" v-model="confirmNewPassword" autocomplete="new-password" @enter="updatePassword()" />
                <div class="flex items-end justify-end cursor-pointer" @click="switchVisibility()" >
                  <img src="@/assets/icons/eye.svg"
                  class="absolute cursor-pointer h-6 w-6 right-4 top-2 fill-current" v-if="passwordFieldType === 'password'">
                  <img src="@/assets/icons/hide-eye.svg"
                class="absolute cursor-pointer h-6 w-6 right-4 top-2" v-else>
                </div>
              </div>
            </div>
          <div class="flex flex-wrap w-full item-center justify-center py-2" v-if="showNotification">
            <hireful-notification :msg="notificationMessage"></hireful-notification>
          </div>
          <div class="flex w-full items-center justify-center py-4">
            <loader v-if="loadingPasswordChange" />
            <button v-else class="bg-hireful text-white w-full hireful-font-semibold h-12 rounded-lg" @click="updatePassword()" >
              Confirm password
            </button>
          </div>
        </div>
        <div class="flex flex-wrap w-full items-center justify-center" v-else>
          <select-account :accounts="accountsResponse" />
        </div>
      </div>
      </div>
    </div>
</template>

<script>

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CookieStorage
} from 'amazon-cognito-identity-js'
import axios from 'axios'
import store from '@/store'
import loader from '@/components/loader'
import selectAccount from '@/components/selectAccount'
import hirefulNotification from '@/components/hireful-notification'
import enableMfa from '@/components/enableMfa'
import setupMfa from '@/components/setupMfa'
import loginMfaStep from '@/components/loginMfaStep'
import textField from '@/components/textField'
import { PublicClientApplication } from '@azure/msal-browser'

export default {
  name: 'Login',
  components: {
    loader,
    selectAccount,
    hirefulNotification,
    enableMfa,
    setupMfa,
    loginMfaStep,
    textField
  },
  data () {
    return {
      user: {
        email: this.$route.query.email || '',
        password: ''
      },
      showNotification: false,
      notificationMessage: '',
      loadingLogin: false,
      microsoftLoading: false,
      googleLoading: false,
      passwordFieldType: 'password',
      loggedIn: false,
      cognitoUser: null,
      userRecord: {},
      isNewUser: false,
      newPassword: '',
      confirmNewPassword: '',
      loadingPasswordChange: false,
      sessionUserAttributes: null,
      clientList: null,
      hirefulHost: process.env.VUE_APP_HIREFUL_APP_HOST,
      hirefulDomain: process.env.VUE_APP_HIREFUL_APP_DOMAIN,
      multiAccountLogin: false,
      activeClientList: null,
      accountsResponse: {},
      logoutReason: this.$route.query.reason || false,
      queryObj: this.$route.query,
      pageTitle: '',
      setupMFA: false,
      userMfaSettings: null,
      configureMfa: false,
      isMfaRequired: false,
      mfaCode: '',
      mfaType: null,
      mfaDetails: null,
      loadingMfaCheck: false,
      rememberDeviceFlag: false,
      loadingMFA: false,
      isMfaEnforced: false,
      isMfaCompleted: false,
      resendingMfa: false,
      parsedParams: {}
    }
  },
  async created () {
    this.$route.path.substring(1).split('&')
      .map(part => part.replace(/^#/, ''))
      .forEach(param => {
        const parts = param.split('=')
        this.parsedParams[parts[0]] = parts[1]
      })
    if (this.parsedParams.access_token && this.parsedParams.state.startsWith('Google')) {
      this.googleLoading = true
      this.pageTitle = this.parsedParams.state === 'Google-switch-account' ? 'please select your account to log in'
        : 'logging in with google'
      this.setLoginOriginCookie('google')
      this.pageTitle = 'logging in with google'
      this.loginSuccess({ access_token: this.parsedParams.access_token })
    }
    const msalConfig = {
      auth: {
        clientId: process.env.VUE_APP_MICROSOFT_CLIENT_ID,
        authority: 'https://login.microsoftonline.com/common/'
      }
    }
    this.msalInstance = new PublicClientApplication(msalConfig)
    this.msalInstance
      .handleRedirectPromise()
      .then((tokenResponse) => {
        if (tokenResponse) {
          this.microsoftLoading = true
          this.pageTitle = 'logging in with microsoft'
          this.setLoginOriginCookie('microsoft')
          this.loginSuccess({ access_token: tokenResponse.idToken })
        }
      })
      .catch((error) => {
        console.error(error)
      })
  },
  async mounted () {
    if (this.logoutReason && this.logoutReason === 'CONCURRENT_SESSION') {
      this.showNotification = true
      this.notificationMessage = 'Another user has logged into this account.As only one user can log into an account at any given time,the previous user will now be logged out.If you have only just logged into this account, you may disregard this message'
    } else if (this.logoutReason && this.logoutReason === 'INACTIVE_SESSION') {
      this.showNotification = true
      this.notificationMessage = 'Just to be safe we have logged you out of the system due to inactivity. Please enter your username and password to log in again.'
    } else {
      this.showNotification = false
      this.notificationMessage = ''
    }
    if (Object.keys(this.$route.query).length !== 0) {
      this.$router.replace({ query: null })
    }
    if (this.parsedParams?.loginError === 'noAccount') {
      this.showNotification = true
      this.notificationMessage = 'There is no account with this email'
    }
  },
  methods: {
    async loginUser () {
      const vm = this
      vm.showNotification = false
      vm.notificationMessage = ''
      vm.loadingLogin = true
      var authenticationData = {
        Username: vm.user.email,
        Password: vm.user.password
      }
      var authenticationDetails = new AuthenticationDetails(
        authenticationData
      )
      const poolData = {
        UserPoolId: process.env.VUE_APP_COGNITO_USER_POOL_ID,
        ClientId: process.env.VUE_APP_COGNITO_CLIENT_ID,
        Storage: new CookieStorage({ domain: `.${vm.hirefulDomain}` })
      }
      const userPool = new CognitoUserPool(poolData)
      const userData = {
        Username: vm.user.email,
        Pool: userPool,
        Storage: new CookieStorage({ domain: `.${vm.hirefulDomain}` })
      }
      vm.cognitoUser = new CognitoUser(userData)
      vm.cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async function (result) {
          vm.setLoginOriginCookie('cognito')
          vm.loginSuccess(result)
        },
        onFailure: function (err) {
          vm.loginFailure(err)
        },
        newPasswordRequired: function (userAttributes, requiredAttributes) {
          vm.loginNewPasswordRequired(userAttributes, requiredAttributes)
        },
        mfaRequired: function (codeDeliveryType, codeDeliveryDetails) {
          vm.loginMfaRequired(codeDeliveryType, codeDeliveryDetails)
        }
      })
    },
    async loginMicrosoftUser () {
      await this.msalInstance
        .loginRedirect({
          scopes: ['email'],
          prompt: 'select_account'
        })
    },
    async loginGoogleUser () {
      const cognitoDomain = process.env.VUE_APP_COGNITO_DOMAIN
      const cognitoClientId = process.env.VUE_APP_COGNITO_CLIENT_ID
      const REDIRECT_URI = `https://login.${process.env.VUE_APP_HIREFUL_APP_DOMAIN}`
      const scope = 'aws.cognito.signin.user.admin%20email%20openid'
      const idp = 'Google'
      window.location.href = `${cognitoDomain}/oauth2/authorize?identity_provider=${idp}&response_type=TOKEN&client_id=${cognitoClientId}&redirect_uri=${REDIRECT_URI}&scope=${scope}&state=${idp}`
    },
    updatePassword () {
      const vm = this
      if (vm.newPassword === vm.confirmNewPassword) {
        vm.loadingPasswordChange = true
        vm.cognitoUser.completeNewPasswordChallenge(vm.newPassword, vm.sessionUserAttributes, {
          onSuccess: async function (result) {
            vm.loginSuccess(result)
          },
          onFailure: function (err) {
            vm.loginFailure(err)
          },
          newPasswordRequired: function (userAttributes, requiredAttributes) {
            vm.loginNewPasswordRequired(userAttributes, requiredAttributes)
          }
        })
      } else {
        vm.showNotification = true
        vm.notificationMessage = 'The passwords dont match'
      }
    },
    switchVisibility () {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password'
    },
    async loginSuccess (result) {
      const vm = this
      const accessToken = result.access_token ? result.access_token : result.getAccessToken().getJwtToken()
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`

      if (vm.rememberDeviceFlag) {
        vm.setRememberDevice()
      }

      document.cookie = `hasLoggedIn=false; domain=.${vm.hirefulDomain};`

      try {
        const userMfaSettingsResp = await axios.get(`${process.env.VUE_APP_LOGIN_API_HOST}${store.state.routes.userMfaSettings}`)
        vm.userMfaSettings = userMfaSettingsResp.data
        if (!vm.userMfaSettings.mfa_enforcement_processed || (!vm.userMfaSettings.mfa_enabled && vm.userMfaSettings.mfa_enforcement_required_for_any_company)) {
          vm.setupMFA = true
          if (!vm.userMfaSettings.mfa_enabled && vm.userMfaSettings.mfa_enforcement_required_for_any_company) {
            vm.isMfaEnforced = true
          }
        } else {
          vm.getUserAccounts()
        }
      } catch (err) {
        const cognitoDomain = process.env.VUE_APP_COGNITO_DOMAIN
        const cognitoClientId = process.env.VUE_APP_COGNITO_CLIENT_ID
        const REDIRECT_URI = `https://login.${process.env.VUE_APP_HIREFUL_APP_DOMAIN}`
        window.location.href = `${cognitoDomain}/logout?client_id=${cognitoClientId}&logout_uri=${REDIRECT_URI}#/loginError=noAccount`
      }
    },
    async getUserAccounts () {
      const vm = this
      const withCredentialsConfig = {
        method: 'GET'
      }
      const resp = await axios(`${process.env.VUE_APP_LOGIN_API_HOST}${store.state.routes.getClientsList}`, withCredentialsConfig)

      vm.accountsResponse = resp.data
      vm.clientList = resp.data.accessibleATSList
      vm.postLogin()
      vm.loggedIn = true
      vm.loadingLogin = false
      vm.microsoftLoading = false
      vm.googleLoading = false
      vm.loadingPasswordChange = false
      vm.setupMFA = false
      vm.isMfaEnforced = false
    },
    loginFailure (err) {
      const vm = this
      vm.showNotification = true
      vm.notificationMessage = err.message
      vm.loadingLogin = false
      vm.loadingPasswordChange = false
    },
    loginNewPasswordRequired (userAttributes, requiredAttributes) {
      const vm = this
      vm.showNotification = false
      vm.notificationMessage = ''
      vm.isNewUser = true
      vm.pageTitle = 'Please provide a new password'
      vm.loadingLogin = false

      // the api doesn't accept this field back
      delete userAttributes.email_verified

      // store userAttributes on global variable
      vm.sessionUserAttributes = userAttributes
    },
    postLogin () {
      const vm = this
      vm.activeClientList = vm.clientList.filter(c => !c.is_disabled)
      if (vm.activeClientList.length) {
        if (vm.activeClientList.length === 1) {
          if (!vm.microsoftLoading && !vm.googleLoading) {
            vm.pageTitle = 'please wait while we log you in'
          }
          vm.redirectToAts(vm.activeClientList[0].token_id)
        } else {
          vm.multiAccountLogin = true
          vm.pageTitle = 'please select your account to log in'
        }
      } else if (vm.accountsResponse.is_internal_user) {
        vm.pageTitle = 'please select a system to log in'
        vm.multiAccountLogin = true
      } else {
        vm.showNotification = true
        vm.notificationMessage = 'Your account might be disabled. Please contact support for more help.'
        vm.loadingLogin = false
        vm.microsoftLoading = false
        vm.googleLoading = false
        vm.loadingPasswordChange = false
      }
    },
    setPageTitle (title) {
      const vm = this
      vm.pageTitle = title
    },
    setupMfa () {
      const vm = this
      vm.configureMfa = true
    },
    async skipMfa () {
      const vm = this
      const payload = { mfa_enforcement_processed: true }
      await axios.post(`${process.env.VUE_APP_LOGIN_API_HOST}${store.state.routes.userMfaSettings}`, payload)
      vm.getUserAccounts()
    },
    resetMfa () {
      const vm = this
      vm.configureMfa = false
    },
    continueLoginAfterMfaSetup () {
      const vm = this
      vm.getUserAccounts()
    },
    loginMfaRequired (codeDeliveryType, codeDeliveryDetails) {
      const vm = this
      vm.showNotification = false
      vm.notificationMessage = ''
      vm.loadingLogin = false
      vm.isMfaRequired = true
      vm.mfaType = codeDeliveryType
      vm.mfaDetails = codeDeliveryDetails
      if (vm.resendingMfa) {
        vm.resendingMfa = false
      }
    },
    submitMfaCode (signInCode) {
      const vm = this
      vm.showNotification = false
      vm.notificationMessage = ''
      vm.loadingMfaCheck = true
      vm.cognitoUser.sendMFACode(signInCode, {
        onSuccess: async function (result) {
          vm.pageTitle = 'please wait while we log you in'
          vm.isMfaCompleted = true
          vm.loadingMfaCheck = false
          vm.loginSuccess(result)
        },
        onFailure: function (err) {
          vm.loadingMfaCheck = false
          vm.loginFailure(err)
        }
      })
    },
    rememberDevice (flag) {
      const vm = this
      vm.rememberDeviceFlag = flag
    },
    async setRememberDevice () {
      const vm = this
      vm.cognitoUser.setDeviceStatusRemembered({
        onSuccess: function (result) {
          console.log('call result: ' + result)
        },
        onFailure: function (err) {
          alert(err.message || JSON.stringify(err))
        }
      })
    },
    resendMfaCode () {
      const vm = this
      vm.resendingMfa = true
      vm.cognitoUser.signOut()
      vm.loginUser()
    },
    setLoginOriginCookie (origin) {
      document.cookie = `loginOrigin=${origin}; domain=.${this.hirefulDomain}`
    }
  }
}
</script>

<style>
.hireful-logo-container {
    height: 35px;
    width: 100%;
}

.hireful-login-container {
    width: 300px;
    box-shadow: 0 4px 30px rgb(0 0 0 / 50%);
    border-radius: 4px!important;
}
.inlineBlock {
  display: inline-block;
}

/* #santa-hat {
  position: absolute;
  left: calc(50vw - 115px);
  top: 1vh;
  transform: scaleX(-1);
} */
</style>
